import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { useAppSelector } from "@/store/hooks";

// Leaving the current deck destroys exactly one thing that is not already on disk: an
// uncommitted stylesheet draft (TODO §12.2.3 / §12.7.5).
//
// `styleDraft` is session-only — never persisted, and dropped by `hydrateStore` on the way
// into the next deck. Everything else the editor holds is either persisted already or pure
// viewport state that deserves to be discarded on a switch, so the draft is the only thing
// worth stopping for, and it gets the same prompt as closing the style panel does.
//
// Returns "may we leave?", so a caller that must do work *before* navigating (minting a new
// deck, say) can ask first and not leave an orphan behind when the user cancels.
export function useLeaveGuard(): () => boolean {
  const styleDirty = useAppSelector((s) => s.styleDraft.draft !== null);
  return useCallback(
    () => !styleDirty || window.confirm("Discard unsaved style changes?"),
    [styleDirty],
  );
}

// The common case: ask, then go. Cancelling stays exactly where we are — no navigation, no
// draft change.
export function useGuardedNavigate(): (to: string) => void {
  const navigate = useNavigate();
  const mayLeave = useLeaveGuard();
  return useCallback(
    (to: string) => {
      if (mayLeave()) navigate(to);
    },
    [mayLeave, navigate],
  );
}
